import { zodResolver } from '@hookform/resolvers/zod';
import type { Resolver } from 'react-hook-form';
import { z } from 'zod';

// The cap on a class's yearly fee is the cap on anything one student can be
// charged or pay against it — one number, not two free to drift apart.
import { MAX_FEE } from '@/features/institute/classes/schemas/class.schema';
import type { FeePaymentMethod } from '@/types/fees';

/**
 * What the fee counter must look like before anything is saved.
 *
 * Mirrors the backend's `fees.validation.ts` — the server is the authority and
 * checks every one of these again; these rules only save a round trip and put
 * the message beside the box that is wrong.
 *
 * Two rules are deliberately left to the server, because the browser cannot
 * know them: whether a payment fits inside what is still outstanding, and
 * whether a fee is being set below what has already been collected. Both depend
 * on the account as it stands at the moment of saving, which is exactly the
 * thing a second clerk may be changing.
 */

/**
 * Money as the form holds it: the string an `<input type="number">` gives,
 * leaving as whole rupees.
 *
 * Blank means zero rather than an error. A family paying only their books fee
 * leaves the tuition box empty, and making them type a nought would be a
 * validation error on the ordinary case.
 */
const amount = z
  .string()
  .trim()
  .transform((value) => (value === '' ? '0' : value))
  .refine((value) => /^\d+$/.test(value), 'Whole rupees only')
  .refine((value) => Number(value) <= MAX_FEE, 'That looks too high — check the amount')
  .transform(Number);

/** The `YYYY-MM-DD` a date input holds, and the API takes. */
const day = z
  .string()
  .trim()
  .min(1, 'Pick a date')
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-08-15');

export const FEE_PAYMENT_METHODS = [
  'CASH',
  'UPI',
  'CARD',
  'NETBANKING',
  'CHEQUE',
  'DEMAND_DRAFT',
  'BANK_TRANSFER',
] as const;

// ---------------------------------------------------------------------------
// what a student is charged
// ---------------------------------------------------------------------------

export const feeChargeSchema = z
  .object({
    tuitionFee: amount,
    tuitionDiscount: amount,
    booksFee: amount,
    booksDiscount: amount,
    otherFee: amount,
    otherDiscount: amount,
    concessionReason: z
      .string()
      .trim()
      .max(120, 'Keep the reason under 120 characters')
      .transform((value) => value || undefined),
    /**
     * When these fees fall due. Optional, and blank clears it.
     *
     * Mirrors the server, which is still the authority (§6): an institute that
     * does not work to due dates leaves it alone, and one that set a date by
     * mistake empties the box to take it off again.
     */
    dueDate: z
      .string()
      .trim()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-08-15')
      .refine((value) => {
        const parsed = new Date(`${value}T00:00:00.000Z`);
        return !Number.isNaN(parsed.getTime()) && parsed.toISOString().startsWith(value);
      }, 'That date does not exist')
      .or(z.literal(''))
      .transform((value) => value || undefined),
  })
  .superRefine((values, ctx) => {
    const heads = [
      { fee: 'tuitionFee', discount: 'tuitionDiscount' },
      { fee: 'booksFee', discount: 'booksDiscount' },
      { fee: 'otherFee', discount: 'otherDiscount' },
    ] as const;

    for (const head of heads) {
      if (values[head.discount] > values[head.fee]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [head.discount],
          message: 'A concession cannot be more than the fee',
        });
      }
    }

    // A concession is a decision somebody made about one family, and it has to
    // be accountable years later.
    const hasConcession =
      values.tuitionDiscount > 0 || values.booksDiscount > 0 || values.otherDiscount > 0;
    if (hasConcession && !values.concessionReason) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['concessionReason'],
        message: 'Say why this concession was given',
      });
    }
  });

/** What the form holds while it is being filled in — every field a string. */
export interface FeeChargeFormValues {
  tuitionFee: string;
  tuitionDiscount: string;
  booksFee: string;
  booksDiscount: string;
  otherFee: string;
  otherDiscount: string;
  concessionReason: string;
  dueDate: string;
}

export type FeeChargePayload = z.infer<typeof feeChargeSchema>;

/**
 * `raw` keeps the form holding what the inputs hold. Without it the resolver
 * hands the submit handler the *transformed* values — money already a number —
 * and the `parse()` that builds the payload then rejects them as not strings.
 */
export const feeChargeFormResolver: Resolver<FeeChargeFormValues> = zodResolver(
  feeChargeSchema,
  undefined,
  { raw: true },
);

export const EMPTY_FEE_CHARGE_FORM: FeeChargeFormValues = {
  tuitionFee: '0',
  tuitionDiscount: '0',
  booksFee: '0',
  booksDiscount: '0',
  otherFee: '0',
  otherDiscount: '0',
  concessionReason: '',
  dueDate: '',
};

// ---------------------------------------------------------------------------
// taking money
// ---------------------------------------------------------------------------

export const paymentSchema = z
  .object({
    tuitionAmount: amount,
    booksAmount: amount,
    otherAmount: amount,
    method: z.enum(FEE_PAYMENT_METHODS, {
      errorMap: () => ({ message: 'Choose how the money was paid' }),
    }),
    reference: z
      .string()
      .trim()
      .max(60, 'That reference is too long')
      .transform((value) => value || undefined),
    paidOn: day,
    remarks: z
      .string()
      .trim()
      .max(200, 'Keep the note under 200 characters')
      .transform((value) => value || undefined),
  })
  .superRefine((values, ctx) => {
    if (values.tuitionAmount + values.booksAmount + values.otherAmount <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['tuitionAmount'],
        message: 'Enter what was paid',
      });
    }

    // Everything but cash arrived through something with a number on it, and
    // that number is what the day's takings are reconciled against.
    if (values.method !== 'CASH' && !values.reference) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reference'],
        message: 'Record the transaction or cheque number',
      });
    }
  });

export interface PaymentFormValues {
  tuitionAmount: string;
  booksAmount: string;
  otherAmount: string;
  method: FeePaymentMethod;
  reference: string;
  paidOn: string;
  remarks: string;
}

export type PaymentPayload = z.infer<typeof paymentSchema>;

export const paymentFormResolver: Resolver<PaymentFormValues> = zodResolver(
  paymentSchema,
  undefined,
  { raw: true },
);
